import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

import { DriverProvider, useDriver } from '../../contexts/DriverContext';
import driverPickupService from '../../services/driverPickupService';

function DriverRouteSheetMobileSignatureContent() {
  const navigate = useNavigate();
  const { pickupId } = useParams();
  const { employee, loading: driverLoading } = useDriver();
  const canvasRef = useRef(null);

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [signatureCompleted, setSignatureCompleted] = useState(false);

  // Load pickup task details
  useEffect(() => {
    if (pickupId) {
      loadTaskDetails();
    }
  }, [pickupId]);

  const loadTaskDetails = async () => {
    try {
      setLoading(true);
      const taskData = await driverPickupService?.getById(pickupId);
      setTask(taskData);

      // Check if signature already exists
      if (taskData?.clientSignatureUrl) {
        setSignatureCompleted(true);
      }
    } catch (err) {
      setError('Erreur lors du chargement de la collecte. Veuillez vérifier le lien.');
      console.error('Error loading task:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    if (canvasRef?.current) {
      canvasRef?.current?.clear();
    }
  };

  const saveSignature = async () => {
    if (!canvasRef?.current || canvasRef?.current?.isEmpty()) {
      setError('Veuillez signer avant de valider');
      return;
    }

    try {
      setSaving(true);
      setError('');

      // Convert canvas to blob
      const canvas = canvasRef?.current?.getCanvas();
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });

      // Upload signature
      await driverPickupService?.uploadSignature(pickupId, blob);

      setSignatureCompleted(true);
      
      // Reload task to get updated signature URL
      await loadTaskDetails();
    } catch (err) {
      setError('Erreur lors de l\'enregistrement de la signature. Veuillez réessayer.');
      console.error('Error saving signature:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error && !task) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">Erreur</h2>
            <p className="mt-2 text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (signatureCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">Signature enregistrée</h2>
            <p className="mt-2 text-gray-600">La collecte a été validée avec succès</p>

            {task?.clientSignatureUrl && (
              <div className="mt-6 border-2 border-gray-200 rounded-lg p-2">
                <img 
                  src={task?.clientSignatureUrl} 
                  alt="Signature client"
                  className="w-full h-auto"
                />
              </div>
            )}

            <div className="mt-6 bg-gray-50 rounded-lg p-4 text-left">
              <h3 className="font-medium text-gray-900 mb-2">Détails de la collecte</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p><span className="font-medium">Client:</span> {task?.clientName}</p>
                <p><span className="font-medium">Adresse:</span> {task?.clientAddress}, {task?.clientCity}</p>
                <p><span className="font-medium">Date:</span> {new Date(task?.date)?.toLocaleDateString('fr-FR')}</p>
                <p><span className="font-medium">Statut:</span> <span className="text-green-600 font-medium">Terminé</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-green-600 text-white py-4 px-4 shadow-md">
        <h1 className="text-xl font-bold">ADT Express - Signature Client</h1>
        <p className="text-sm text-green-100 mt-1">Collecte #{task?.id?.slice(0, 8)}</p>
      </div>
      <div className="max-w-2xl mx-auto p-4 pb-24">
        {/* Task Details Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <svg className="h-5 w-5 mr-2 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Informations Client
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Nom du client</p>
              <p className="text-base font-medium text-gray-900">{task?.clientName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Téléphone</p>
              <p className="text-base text-gray-900">{task?.clientPhone || 'Non renseigné'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Adresse</p>
              <p className="text-base text-gray-900">
                {task?.clientAddress}<br />
                {task?.clientCity} {task?.clientZone && `(${task?.clientZone})`}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="text-base text-gray-900">{new Date(task?.date)?.toLocaleDateString('fr-FR')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Créneau</p>
                <p className="text-base text-gray-900">{task?.timeWindow || 'Non défini'}</p>
              </div>
            </div>
            {task?.driver && (
              <div>
                <p className="text-sm text-gray-500">Chauffeur</p>
                <p className="text-base text-gray-900">{task?.driver?.name}</p>
              </div>
            )}
            {task?.notes && (
              <div>
                <p className="text-sm text-gray-500">Notes</p>
                <p className="text-base text-gray-900">{task?.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Signature Pad Card */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <svg className="h-5 w-5 mr-2 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Signature du Client
          </h2>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Veuillez demander au client de signer dans l'espace ci-dessous pour confirmer la collecte.
            </p>
          </div>

          {/* Signature Canvas */}
          <div className="border-2 border-gray-300 rounded-lg overflow-hidden mb-4 bg-white">
            <SignatureCanvas
              ref={canvasRef}
              canvasProps={{
                className: 'w-full h-64 touch-action-none',
                style: { touchAction: 'none' }
              }}
              backgroundColor="white"
              penColor="black"
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={clearSignature}
              disabled={saving}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Effacer
            </button>
            <button
              onClick={saveSignature}
              disabled={saving}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {saving ? (
                <>
                  <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-white border-r-transparent mr-2"></div>
                  Enregistrement...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Valider la Signature
                </>
              )}
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            En validant, la collecte sera automatiquement marquée comme terminée
          </p>
        </div>
      </div>
    </div>
  );
}

// Wrap the component with DriverProvider
export default function DriverRouteSheetMobileSignature() {
  return (
    <DriverProvider>
      <DriverRouteSheetMobileSignatureContent />
    </DriverProvider>
  );
}